import React from 'react';
import { FormattedMessage } from 'react-intl';
import { FormRenderProps } from 'react-final-form';

import PageBaseProps from 'types/PageBase';
import FormKeys from 'modules/form/types/FormKeys';
import type { RepairOrderForm as RepairOrderFormType } from 'types/RepairOrderForm';

import { useSelector } from 'hooks';
import useUpload from 'hooks/useUpload';
import { useGetSelfServiceByIdQuery, useUpdateSelfServiceMutation } from 'modules/selfServices/service';

import { UPDATE_FIXED_CACHE_KEY } from 'modules/selfServices/constants';

import { getSelectedSelfServiceId } from 'modules/auth/selectors';

import PageFooter from 'components/ui/PageFooter';

import useRepairOrder from './useRepairOrder';
import CustomerActions from './CustomerActions';
import ProactiveWorks from './ProactiveWorks';
import RepairOrderExpandableList from './RepairOrderExpandableList';

type RepairOrderFormProps =
  FormRenderProps<RepairOrderFormType> & Pick<PageBaseProps, 'onPrev' | 'shouldDisplayBackButton' | 'isPenultimate'> & {
    formKey: FormKeys
  };

const RepairOrderForm: React.FC<RepairOrderFormProps> = ({
  onPrev, handleSubmit, shouldDisplayBackButton, isPenultimate, formKey, hasValidationErrors,
}) => {
  const selfServiceId = useSelector(getSelectedSelfServiceId);

  const { isLoading: isUploading } = useUpload(selfServiceId);
  const { data: selfService } = useGetSelfServiceByIdQuery({ id: selfServiceId });

  const { hasRepairOrder, sortedRepairOrderItems } = useRepairOrder(selfService);

  const { proactiveChecklists } = selfService;
  const hasProactiveChecklist = proactiveChecklists?.length > 0;
  const showCustomerActionsDivider = hasRepairOrder || hasProactiveChecklist;

  const [, { isLoading: isUpdateLoading }] = useUpdateSelfServiceMutation({ fixedCacheKey: UPDATE_FIXED_CACHE_KEY });

  const isLoading = isUploading || isUpdateLoading;

  return (
    <>
      <main className="content">
        <div className="flex flex-col gap-l items-center w-full">
          {hasRepairOrder && <RepairOrderExpandableList data={sortedRepairOrderItems} />}
          {hasProactiveChecklist && (
            <>
              {hasRepairOrder && <div className="h-px w-full bg-border-default max-w-content" />}
              <ProactiveWorks formKey={formKey} />
            </>
          )}
          {showCustomerActionsDivider && <div className="h-px w-full bg-border-default" />}
          <CustomerActions />
        </div>
      </main>
      <PageFooter
        onPrev={onPrev}
        loading={isLoading}
        onNext={handleSubmit}
        isPenultimate={isPenultimate}
        disabledStyle={hasValidationErrors}
        shouldDisplayBackButton={shouldDisplayBackButton}
        hint={hasProactiveChecklist && (
          <FormattedMessage
            id="proactiveWorks.footer.hint"
            defaultMessage="Proactive works are subject to parts and technician availability."
          />
        )}
      />
    </>
  );
};

export default RepairOrderForm;
